import { type PromptUploadResult, promptsApi } from '../api/prompts'

export type PromptUploadItemStatus = 'pending' | 'uploading' | 'complete' | 'failed'

export interface PromptUploadItem {
  key: string
  file: File
  status: PromptUploadItemStatus
  error?: string
  result?: PromptUploadResult
}

export interface PromptUploadBatchSummary {
  complete: number
  failed: number
  total: number
}

const TRANSIENT_STATUS_CODES = new Set([408, 429, 500, 502, 503, 504])

function fileKey(file: File): string {
  return `${file.name}::${file.size}::${file.lastModified}`
}

function sleep(ms: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, ms))
}

function isTransientError(error: unknown): boolean {
  if (error && typeof error === 'object' && 'status' in error) {
    return TRANSIENT_STATUS_CODES.has((error as { status: number }).status)
  }
  return error instanceof TypeError
}

export function createPromptUploadItems(files: File[]): PromptUploadItem[] {
  return files.map((file) => ({ key: fileKey(file), file, status: 'pending' }))
}

/**
 * Uploads a batch of Markdown files into a project's prompt library. Unlike
 * {@link uploadBatch} (RAG documents), there is no docType, no dedupe-by-hash
 * "skipped" state, and no follow-up indexing step — each upload upserts a
 * prompt row directly.
 */
export async function uploadPromptBatch(
  items: PromptUploadItem[],
  options: {
    projectId: number
    concurrency?: number
    maxRetries?: number
    onItemUpdate?: (item: PromptUploadItem) => void
  }
): Promise<{ items: PromptUploadItem[]; summary: PromptUploadBatchSummary }> {
  const { projectId, concurrency = 3, maxRetries = 3, onItemUpdate } = options

  const working = items.map((item) => ({ ...item }))
  const queue = [...working]
  const inFlight: Promise<void>[] = []

  const updateItem = (key: string, patch: Partial<PromptUploadItem>) => {
    const index = working.findIndex((item) => item.key === key)
    if (index === -1) return
    working[index] = { ...working[index]!, ...patch }
    onItemUpdate?.(working[index]!)
  }

  const uploadOne = async (item: PromptUploadItem) => {
    updateItem(item.key, { status: 'uploading', error: undefined })

    let lastError: string | undefined
    for (let attempt = 1; attempt <= maxRetries; attempt++) {
      try {
        const result = await promptsApi.uploadMarkdown(projectId, item.file)
        updateItem(item.key, { status: 'complete', result })
        return
      } catch (error) {
        lastError = error instanceof Error ? error.message : 'Upload failed'
        if (!isTransientError(error) || attempt === maxRetries) break
        await sleep(500 * 2 ** (attempt - 1))
      }
    }

    updateItem(item.key, { status: 'failed', error: lastError ?? 'Upload failed' })
  }

  while (queue.length > 0 || inFlight.length > 0) {
    while (queue.length > 0 && inFlight.length < concurrency) {
      const item = queue.shift()!
      const task = uploadOne(item).finally(() => {
        const idx = inFlight.indexOf(task)
        if (idx >= 0) inFlight.splice(idx, 1)
      })
      inFlight.push(task)
    }
    if (inFlight.length > 0) {
      await Promise.race(inFlight)
    }
  }

  const summary: PromptUploadBatchSummary = {
    total: working.length,
    complete: working.filter((item) => item.status === 'complete').length,
    failed: working.filter((item) => item.status === 'failed').length,
  }

  return { items: working, summary }
}
